import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSource, useSources, useUpdateSource, useDeleteSource, useSourceTypeConfig, useDataLoads } from '../../hooks/useSource';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { useFiles } from '../../hooks/useFiles';
import { useAvailableSnowflakeTables, type SnowflakeTableOption } from '../../hooks/useSnowflakeTables';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { sourceService, getSourceCategory, UNIONED_SOURCE_TYPES, type SourceCategory } from '../../services/source.service';
import { SourceIcon } from '../../components/SourceIcon';
import { fileService } from '../../services/file.service';
import type { FileRecord } from '../../services/file.service';
import { pipelineNodeService, type PipelineNode } from '../../services/pipelineNode.service';
import { getPipelineNodeIcon } from '../../components/PipelineNodeIcon';
import { summarizeNumber } from '../../services/formatting.service';
import { SyncPollIndicator } from '../../components/sources/SyncPollIndicator';
import BackgroundService from '../../services/bg.service';
import { S3Uploader, type UploadedFile } from '../../components/s3uploader/S3Uploader';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { NodeDataTable } from '../../components/NodeDataTable';
import { SourceDataGate } from '../../components/SourceDataGate';
import { RebuildModal } from '../../components/RebuildModal';
import { BuildAllModal } from '../../components/BuildAllModal';
import { ConfigFieldEditor } from '../../components/sources/ConfigFieldEditor';
import { DiscoverButton } from '../../components/sources/DiscoverButton';
import { ConfigFieldDescription } from '../../components/sources/ConfigFieldDescription';
import { SyncScheduleSelect } from '../../components/sources/SyncScheduleSelect';
import { SyncHistoryTable } from '../../components/sources/SyncHistoryTable';
import { FullResyncDialog, type FullResyncOptions } from '../../components/sources/FullResyncDialog';
import { RequireRole } from '../../components/RequireRole';
import { Loader2, Search, Database, Settings, Download, Trash2, Upload, Plus, ChevronRight, ChevronDown, ArrowLeft, RefreshCw, Play, Link2, ShieldCheck, ShieldAlert, ChevronDownIcon, Save, AlertTriangle, Hammer } from 'lucide-react';
import { toast } from 'sonner';

function getSourceTypeBadge(sourceType: string) {
  const category = getSourceCategory(sourceType);
  switch (category) {
    case 'flat_file':
      return (
        <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400 dark:border-green-400">
          Flat File
        </Badge>
      );
    case 'snowflake':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400 dark:border-blue-400">
          Snowflake
        </Badge>
      );
    case 'managed_integration':
      return (
        <Badge variant="secondary">
          Managed Integration
        </Badge>
      );
  }
}


function getStatusBadge(status: string) {
  switch (status) {
    case 'LOADED':
      return <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400">Loaded</Badge>;
    case 'PRE_PROCESSED':
      return <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400">Processed</Badge>;
    case 'UPLOADING':
      return <Badge variant="outline" className="border-yellow-500 text-yellow-700 dark:text-yellow-400">Uploading</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

interface NodeFilesProps {
  node: PipelineNode;
  sourceId: string;
}

function NodeFiles({ node, sourceId }: NodeFilesProps) {
  const queryClient = useQueryClient();
  const { data: filesData, isLoading: isLoadingFiles } = useFiles(node.source_record_type_id);
  const [showUploader, setShowUploader] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<null | 'creating_srt' | 'processing' | 'loading' | 'error'>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FileRecord | null>(null);
  const [deleteState, setDeleteState] = useState<'confirm' | 'deleting' | 'error'>('confirm');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Request link state
  const [requestLinkOpen, setRequestLinkOpen] = useState(false);
  const [requestLinkExpiry, setRequestLinkExpiry] = useState(30);
  const [creatingRequestLink, setCreatingRequestLink] = useState(false);

  const files = useMemo(() => {
    const records = filesData?.records || [];
    return [...records].sort((a, b) => {
      if (a.uploaded_at > b.uploaded_at) return -1;
      return 1;
    });
  }, [filesData?.records]);

  const handleUploadComplete = useCallback(async (file: UploadedFile) => {
    setUploadStatus('processing');
    setUploadError(null);
    try {
      // Process the file to extract headers/parts
      const processResp = await fileService.process(file.id);
      const parts = await BackgroundService.getInstance().waitForJob(processResp.job_id) as any[];

      if (!parts || parts.length === 0) {
        setUploadError('No data found in file.');
        setUploadStatus('error');
        return;
      }

      const part = parts[0];
      let srtId = node.source_record_type_id;

      // Auto-create a SourceRecordType if the node doesn't have one yet
      if (!srtId) {
        setUploadStatus('creating_srt');
        const srtResult = await fileService.createSourceRecordType({
          name: node.label || node.name || file.name,
          sourceId,
          columns: part.headers,
        });
        srtId = srtResult.id;

        // Update the pipeline node with the new SRT ID
        await pipelineNodeService.save({
          id: node.id,
          source_record_type_id: srtId,
        } as any);
        queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      }

      // Load the first part into Snowflake
      setUploadStatus('loading');
      const loadResp = await fileService.loadPart(
        part.file_id,
        part.id,
        srtId,
        part.headers,
        node.id
      );
      await BackgroundService.getInstance().waitForJob(loadResp.job_id);

      // Refresh file list and node data
      queryClient.invalidateQueries({ queryKey: ['files'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      setUploadStatus(null);
      setShowUploader(false);
    } catch (err) {
      console.error('File upload/process error:', err);
      setUploadError(err instanceof Error ? err.message : 'Failed to process file');
      setUploadStatus('error');
    }
  }, [node.source_record_type_id, node.id, node.label, node.name, sourceId, queryClient]);

  const handleDownload = useCallback(async (fileId: string) => {
    await fileService.downloadOriginal(fileId);
  }, []);

  const openDeleteDialog = useCallback((file: FileRecord) => {
    setDeleteTarget(file);
    setDeleteState('confirm');
    setDeleteError(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteState('deleting');
    setDeleteError(null);
    try {
      const resp = await fileService.deleteFromNode(node.id, deleteTarget.id);
      if (resp.job_id) {
        await BackgroundService.getInstance().waitForJob(resp.job_id);
      }
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete file:', err);
      setDeleteState('error');
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete file');
    }
  }, [deleteTarget, node.id, queryClient]);

  const closeDeleteDialog = useCallback(() => {
    if (deleteState !== 'deleting') {
      setDeleteTarget(null);
    }
  }, [deleteState]);

  const handleCreateRequestLink = useCallback(async () => {
    setCreatingRequestLink(true);
    try {
      const nodeName = node.label || node.name || 'file';
      const url = await sourceService.createFlatFileUploadLink(
        sourceId,
        nodeName,
        '',
        true,
        false,
        node.id,
        requestLinkExpiry,
      );
      await navigator.clipboard.writeText(url);
      toast.success('Upload link copied to clipboard');
      setRequestLinkOpen(false);
      setRequestLinkExpiry(30);
    } catch (err) {
      toast.error('Failed to create upload link');
      console.error(err);
    } finally {
      setCreatingRequestLink(false);
    }
  }, [sourceId, node.id, node.label, node.name, requestLinkExpiry]);

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        To replace the contents of this source, delete the existing file and upload a new one. To append data, upload a new file with the same headers.
      </p>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-foreground">Files</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRequestLinkOpen(true)}
          >
            <Link2 className="h-3.5 w-3.5 mr-1.5" />
            Request Link
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowUploader(!showUploader)}
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            Upload New
          </Button>
        </div>
      </div>

      {showUploader && (
        <div className="mb-4">
          {uploadStatus === 'processing' && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md mb-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Processing file...</span>
            </div>
          )}
          {uploadStatus === 'creating_srt' && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md mb-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Setting up data source...</span>
            </div>
          )}
          {uploadStatus === 'loading' && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-md mb-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Loading data into Snowflake...</span>
            </div>
          )}
          {uploadStatus === 'error' && uploadError && (
            <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md mb-2">
              <p className="text-sm text-destructive">{uploadError}</p>
            </div>
          )}
          {!uploadStatus && (
            <S3Uploader onUploadComplete={handleUploadComplete} />
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Supported formats: CSV, Excel (XLSX), TSV. Max file size: 250MB.
          </p>
        </div>
      )}

      {isLoadingFiles ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      ) : files.length === 0 ? (
        <p className="text-sm text-muted-foreground">No files uploaded yet.</p>
      ) : (
        <div className="border rounded-md divide-y">
          {files.map((file) => (
            <div key={file.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex-1 min-w-0 mr-4">
                <div className="text-sm font-medium text-foreground truncate">{file.name}</div>
                <div className="text-xs text-muted-foreground">{timeAgo(file.uploaded_at)}</div>
                {file.description && (
                  <div className="text-xs text-muted-foreground mt-0.5 truncate" title={file.description}>
                    {file.description.length > 80 ? file.description.slice(0, 80) + '...' : file.description}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(file.processing_status)}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(file.id)}
                  title="Download"
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openDeleteDialog(file)}
                  title="Delete"
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) closeDeleteDialog(); }}>
        <DialogContent hideClose={deleteState === 'deleting'} onInteractOutside={(e) => { if (deleteState === 'deleting') e.preventDefault(); }} onEscapeKeyDown={(e) => { if (deleteState === 'deleting') e.preventDefault(); }}>
          {deleteState === 'confirm' && (
            <>
              <DialogHeader>
                <DialogTitle>Delete File</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete <strong>{deleteTarget?.name}</strong> and all its records? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteDialog}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </Button>
              </DialogFooter>
            </>
          )}
          {deleteState === 'deleting' && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Deleting <strong>{deleteTarget?.name}</strong> and clearing records...</p>
            </div>
          )}
          {deleteState === 'error' && (
            <>
              <DialogHeader>
                <DialogTitle>Delete Failed</DialogTitle>
                <DialogDescription>
                  Failed to delete <strong>{deleteTarget?.name}</strong>.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md">
                  <p className="text-sm text-destructive">{deleteError}</p>
                </div>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteDialog}>Close</Button>
                <Button variant="destructive" onClick={handleConfirmDelete}>
                  Retry
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={requestLinkOpen} onOpenChange={setRequestLinkOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Private Upload Link</DialogTitle>
            <DialogDescription>
              Anyone with this private link can upload files to <strong>{node.label || node.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="request-link-expiry">Link expires after</Label>
              <select
                id="request-link-expiry"
                value={requestLinkExpiry}
                onChange={(e) => setRequestLinkExpiry(Number(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={180}>6 months</option>
                <option value={365}>1 year</option>
              </select>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestLinkOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateRequestLink} disabled={creatingRequestLink}>
              {creatingRequestLink ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Private Link'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const WARN_BEFORE_OAUTH_EXPIRY_DAYS = 7;

/**
 * Warning to show about an access token that is running out.
 *
 * Only set for providers whose tokens can't be refreshed (Otter's last ~30 days
 * and have no refresh token), where lapsing quietly stops the source syncing and
 * only a human re-authorizing fixes it. Returns null when there's nothing worth
 * saying yet.
 */
function oauthExpiryWarning(expiresAt?: string): { message: string; expired: boolean } | null {
  if (!expiresAt) return null;
  const msRemaining = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(msRemaining)) return null;

  if (msRemaining <= 0) {
    return { message: 'Authorization expired — re-authorize to resume syncing.', expired: true };
  }

  const daysRemaining = Math.ceil(msRemaining / 86400000);
  if (daysRemaining > WARN_BEFORE_OAUTH_EXPIRY_DAYS) return null;
  return {
    message: `Authorization expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} — re-authorize to avoid an interruption.`,
    expired: false,
  };
}

export default function SourceDetail() {
  const { sourceId, nodeId: selectedNodeId } = useParams<{ sourceId: string; nodeId: string }>();
  const [query, setQuery] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Source editing state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const updateSource = useUpdateSource();
  const [iconUploadModalOpen, setIconUploadModalOpen] = useState(false);

  const isInDraftMode = useIsInDraftMode();

  // Rebuild modal state
  const [rebuildModalOpen, setRebuildModalOpen] = useState(false);
  const [sourceBuildOpen, setSourceBuildOpen] = useState(false);

  // New Table dialog state
  const [advancedConfigOpen, setAdvancedConfigOpen] = useState(false);
  const [creatingSourceNodes, setCreatingSourceNodes] = useState(false);
  const [newTableDialogOpen, setNewTableDialogOpen] = useState(false);
  const [newTableName, setNewTableName] = useState('');
  const [isCreatingTable, setIsCreatingTable] = useState(false);
  const [newTableError, setNewTableError] = useState<string | null>(null);

  // Snowflake table picker state
  const [sfSearch, setSfSearch] = useState('');
  const [sfSelectedTable, setSfSelectedTable] = useState<SnowflakeTableOption | null>(null);
  const [expandedDbs, setExpandedDbs] = useState<Set<string>>(new Set());
  const [expandedSchemas, setExpandedSchemas] = useState<Set<string>>(new Set());

  // Flat file upload state for new table dialog
  const [fileUploadStatus, setFileUploadStatus] = useState<null | 'processing' | 'creating_node' | 'creating_srt' | 'loading'>(null);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);

  // Manage Tables dialog state
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [dialogSearch, setDialogSearch] = useState('');
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const { data: source, isLoading: isLoadingSource } = useSource(sourceId);

  const sourceCategory: SourceCategory | null = useMemo(() => {
    if (!source) return null;
    return getSourceCategory(source.type);
  }, [source]);

  const isFlatFile = sourceCategory === 'flat_file';
  const isSnowflake = sourceCategory === 'snowflake';
  const isManagedIntegration = sourceCategory === 'managed_integration';

  const { data: snowflakeTables, isLoading: isLoadingSnowflakeTables } = useAvailableSnowflakeTables();

  // Managed integration state
  const { data: sourceTypeConfig } = useSourceTypeConfig(isManagedIntegration ? source?.type : undefined);
  const { data: dataLoads, refetch: refetchDataLoads } = useDataLoads(isManagedIntegration ? sourceId : undefined);
  const [config, setConfig] = useState<Record<string, any>>({});
  const [secretConfig, setSecretConfig] = useState<Record<string, any>>({});
  const [syncCronExpression, setSyncCronExpression] = useState('0 0 * * *');
  const [triggeringSync, setTriggeringSync] = useState(false);
  const [showFullResyncDialog, setShowFullResyncDialog] = useState(false);
  const [showSelectiveSyncDialog, setShowSelectiveSyncDialog] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [togglingPause, setTogglingPause] = useState(false);
  const [editingTableNodePrefix, setEditingTableNodePrefix] = useState(false);
  const [editTableNodePrefix, setEditTableNodePrefix] = useState('');
  const [savingPrefix, setSavingPrefix] = useState(false);
  const [prefixError, setPrefixError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const deleteSource = useDeleteSource();

  // Secure config link state
  const [secureConfigDialogOpen, setSecureConfigDialogOpen] = useState(false);
  const [secureConfigSelectedFields, setSecureConfigSelectedFields] = useState<Set<string>>(new Set());
  const [creatingSecureLink, setCreatingSecureLink] = useState(false);

  // Flat file upload link state
  const [flatFileUploadDialogOpen, setFlatFileUploadDialogOpen] = useState(false);
  const [flatFileUploadTitle, setFlatFileUploadTitle] = useState('');
  const [flatFileUploadDescription, setFlatFileUploadDescription] = useState('');
  const [flatFileUploadAllowMultiple, setFlatFileUploadAllowMultiple] = useState(false);
  const [flatFileUploadAskForDescription, setFlatFileUploadAskForDescription] = useState(true);
  const [creatingFlatFileUploadLink, setCreatingFlatFileUploadLink] = useState(false);

  // Polling for sync status
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingIntervalRef = useRef(5000);
  const wasSyncingRef = useRef(false);
  const INITIAL_POLL_INTERVAL = 5000;
  const MAX_POLL_INTERVAL = 60000;
  const MAX_POLL_DURATION = 3 * 60 * 60 * 1000; // 3 hours
  const [currentPollInterval, setCurrentPollInterval] = useState(INITIAL_POLL_INTERVAL);
  const [pollExpired, setPollExpired] = useState(false);
  const pollStartRef = useRef<number>(0);

  const isSyncing = useMemo(() => {
    const status = source?.sync_info?.status;
    return status === 'QUEUED' || status === 'SYNCING';
  }, [source?.sync_info?.status]);

  const isPaused = useMemo(() => {
    if (source?.fivetran) {
      return !!source.fivetran.connector?.paused;
    }
    return !source?.sync_info?.sync_schedule?.enabled;
  }, [source]);

  // Initialize config from source when it loads
  useEffect(() => {
    if (source && isManagedIntegration) {
      setConfig(source.config || {});
      if (source.sync_info?.sync_schedule?.cron_expression) {
        setSyncCronExpression(source.sync_info.sync_schedule.cron_expression);
      }
    }
  }, [source, isManagedIntegration]);

  // Handle OAuth callback query params
  useEffect(() => {
    if (query.get('authorized') === 'true') {
      toast.success('Authorization successful');
      setQuery({});
    } else if (query.get('error') === 'authorization_error') {
      toast.error('Error authorizing connection');
      setQuery({});
    }
  }, [query, setQuery]);

  // Refresh sidebar tables when sync completes (may have created new tables)
  useEffect(() => {
    if (isSyncing) {
      wasSyncingRef.current = true;
    } else if (wasSyncingRef.current) {
      wasSyncingRef.current = false;
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
    }
  }, [isSyncing, queryClient]);

  // Poll for sync status: fetch latest and schedule next poll
  const pollNow = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
    await refetchDataLoads();
  }, [queryClient, sourceId, refetchDataLoads]);

  const schedulePoll = useCallback(() => {
    if (pollingRef.current) clearTimeout(pollingRef.current);
    if (Date.now() - pollStartRef.current >= MAX_POLL_DURATION) {
      setPollExpired(true);
      return;
    }
    const interval = pollingIntervalRef.current;
    setCurrentPollInterval(interval);
    pollingRef.current = setTimeout(async () => {
      await pollNow();
      pollingIntervalRef.current = Math.min(pollingIntervalRef.current * 1.5, MAX_POLL_INTERVAL);
      schedulePoll();
    }, interval);
  }, [pollNow]);

  // Polling effect for sync status
  useEffect(() => {
    if (!isManagedIntegration || !isSyncing) {
      pollingIntervalRef.current = INITIAL_POLL_INTERVAL;
      setCurrentPollInterval(INITIAL_POLL_INTERVAL);
      pollStartRef.current = 0;
      setPollExpired(false);
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    if (!pollStartRef.current) pollStartRef.current = Date.now();
    schedulePoll();

    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isSyncing, isManagedIntegration, schedulePoll]);

  // Immediate check: cancel current timer, poll now, reset interval and 3h window, reschedule
  const handleCheckNow = useCallback(async () => {
    if (pollingRef.current) clearTimeout(pollingRef.current);
    pollStartRef.current = Date.now();
    pollingIntervalRef.current = INITIAL_POLL_INTERVAL;
    setPollExpired(false);
    await pollNow();
    if (isSyncing) schedulePoll();
  }, [pollNow, isSyncing, schedulePoll]);

  const syncButtonText = useMemo(() => {
    if (triggeringSync) return 'Queuing...';
    if (source?.sync_info?.status === 'QUEUED') return 'Queued';
    if (source?.sync_info?.status === 'SYNCING') return 'Syncing';
    return 'Sync Now';
  }, [triggeringSync, source?.sync_info?.status]);

  const handleTriggerSync = useCallback(async () => {
    if (!sourceId) return;
    setTriggeringSync(true);
    try {
      await sourceService.triggerSync(sourceId);
      pollingIntervalRef.current = INITIAL_POLL_INTERVAL;
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      refetchDataLoads();
    } catch (err) {
      toast.error('Failed to trigger sync');
      console.error(err);
    } finally {
      setTriggeringSync(false);
    }
  }, [sourceId, queryClient, refetchDataLoads]);

  const handleFullResync = useCallback(async (options: FullResyncOptions) => {
    if (!sourceId) return;
    setShowFullResyncDialog(false);
    setTriggeringSync(true);
    try {
      await sourceService.triggerSync(sourceId, {
        full_refresh: true,
        drop_schema_first: options.dropSchemaFirst,
        selected_streams: options.selectedStreams || undefined,
      });
      pollingIntervalRef.current = INITIAL_POLL_INTERVAL;
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      refetchDataLoads();
    } catch (err) {
      toast.error('Failed to trigger re-sync');
      console.error(err);
    } finally {
      setTriggeringSync(false);
    }
  }, [sourceId, queryClient, refetchDataLoads]);

  const handleSelectiveSync = useCallback(async (options: FullResyncOptions) => {
    if (!sourceId) return;
    setShowSelectiveSyncDialog(false);
    setTriggeringSync(true);
    try {
      await sourceService.triggerSync(sourceId, {
        selected_streams: options.selectedStreams || undefined,
      });
      pollingIntervalRef.current = INITIAL_POLL_INTERVAL;
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      refetchDataLoads();
    } catch (err) {
      toast.error('Failed to trigger sync');
      console.error(err);
    } finally {
      setTriggeringSync(false);
    }
  }, [sourceId, queryClient, refetchDataLoads]);

  const handleCreateSourceNodes = useCallback(async () => {
    if (!sourceId) return;
    setCreatingSourceNodes(true);
    try {
      await sourceService.createSourceNodes(sourceId);
      toast.success("Creating source tables from synced data — they'll appear shortly.");
      // The job runs async; refresh the tables/nodes view after it's had a moment.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
        queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      }, 5000);
    } catch (err) {
      toast.error('Failed to start source-table creation');
      console.error(err);
    } finally {
      setCreatingSourceNodes(false);
    }
  }, [sourceId, queryClient]);

  const handleSaveConfig = useCallback(async () => {
    if (!sourceId) return;
    setSavingConfig(true);
    try {
      const hasSecureConfig = Object.values(secretConfig).some((v) => !!v);
      await sourceService.initSource(sourceId, {
        source_record_types: [],
        config,
        secure_config: hasSecureConfig ? secretConfig : null,
        sync_info: {
          sync_schedule: {
            cron_expression: syncCronExpression,
          },
        },
      });
      toast.success('Configuration saved');
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
    } catch (err) {
      toast.error('Failed to save configuration');
      console.error(err);
    } finally {
      setSavingConfig(false);
    }
  }, [sourceId, config, secretConfig, syncCronExpression, queryClient]);

  const handleTogglePause = useCallback(async () => {
    if (!sourceId) return;
    setTogglingPause(true);
    try {
      await sourceService.enableSync(sourceId, !isPaused);
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
    } catch (err) {
      toast.error('Failed to update sync schedule');
      console.error(err);
    } finally {
      setTogglingPause(false);
    }
  }, [sourceId, isPaused, queryClient]);

  const handleAuthorize = useCallback(async () => {
    if (!sourceId) return;
    try {
      await handleSaveConfig();
      const url = await sourceService.getAuthorizationUrl(sourceId);
      window.location.href = url;
    } catch (err) {
      toast.error('Failed to start authorization');
      console.error(err);
    }
  }, [sourceId, handleSaveConfig]);

  const handleShareableLink = useCallback(async () => {
    if (!sourceId) return;
    try {
      await handleSaveConfig();
      const url = await sourceService.createShareableAuthUrl(sourceId);
      await navigator.clipboard.writeText(url);
      toast.success('Shareable link copied to clipboard');
    } catch (err) {
      toast.error('Failed to create shareable link');
      console.error(err);
    }
  }, [sourceId, handleSaveConfig]);

  const handleCreateSecureConfigLink = useCallback(async () => {
    if (!sourceId || secureConfigSelectedFields.size === 0) return;
    setCreatingSecureLink(true);
    try {
      const url = await sourceService.createSecureConfigLink(sourceId, Array.from(secureConfigSelectedFields));
      await navigator.clipboard.writeText(url);
      toast.success('Secure link copied to clipboard');
      setSecureConfigDialogOpen(false);
      setSecureConfigSelectedFields(new Set());
    } catch (err) {
      toast.error('Failed to create secure link');
      console.error(err);
    } finally {
      setCreatingSecureLink(false);
    }
  }, [sourceId, secureConfigSelectedFields]);

  const handleCreateFlatFileUploadLink = useCallback(async () => {
    if (!sourceId || !flatFileUploadTitle.trim()) return;
    setCreatingFlatFileUploadLink(true);
    try {
      const url = await sourceService.createFlatFileUploadLink(
        sourceId,
        flatFileUploadTitle,
        flatFileUploadDescription,
        flatFileUploadAllowMultiple,
        flatFileUploadAskForDescription
      );
      await navigator.clipboard.writeText(url);
      toast.success('Upload link copied to clipboard');
      setFlatFileUploadDialogOpen(false);
      setFlatFileUploadTitle('');
      setFlatFileUploadDescription('');
      setFlatFileUploadAllowMultiple(false);
      setFlatFileUploadAskForDescription(true);
    } catch (err) {
      toast.error('Failed to create upload link');
      console.error(err);
    } finally {
      setCreatingFlatFileUploadLink(false);
    }
  }, [sourceId, flatFileUploadTitle, flatFileUploadDescription, flatFileUploadAllowMultiple, flatFileUploadAskForDescription]);

  const handleDeleteSource = useCallback(async () => {
    if (!sourceId || !source || deleteConfirmName !== source.name) return;
    try {
      await deleteSource.mutateAsync(sourceId);
      toast.success('Source deleted');
      navigate('/sources');
    } catch (err) {
      toast.error('Failed to delete source');
      console.error(err);
    }
  }, [sourceId, source, deleteConfirmName, deleteSource, navigate]);

  const changeConfig = useCallback((key: string, val: string) => {
    setConfig((prev) => ({ ...prev, [key]: val }));
  }, []);

  const changeSecretConfig = useCallback((key: string, val: string) => {
    setSecretConfig((prev) => ({ ...prev, [key]: val }));
  }, []);

  // Initialize edit fields when source loads
  useEffect(() => {
    if (source) {
      setEditName(source.name);
      setEditDescription(source.description || '');
    }
  }, [source]);

  const isDirty = useMemo(() => {
    if (!source) return false;
    return editName !== source.name || editDescription !== (source.description || '');
  }, [source, editName, editDescription]);

  const handleSaveSource = useCallback(async () => {
    if (!sourceId || !isDirty) return;
    updateSource.mutate({
      id: sourceId,
      name: editName,
      description: editDescription,
    });
  }, [sourceId, editName, editDescription, isDirty, updateSource]);

  const handleIconUpload = useCallback(async (file: UploadedFile) => {
    if (!sourceId) return;
    try {
      await sourceService.saveSourceIcon(sourceId, file.id);
      // Wait for CDN cache invalidation before refreshing
      await new Promise((resolve) => setTimeout(resolve, 3000));
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      setIconUploadModalOpen(false);
      toast.success('Source icon updated');
    } catch (err: any) {
      toast.error('Failed to save icon', { description: err.message || 'An error occurred' });
    }
  }, [sourceId, queryClient]);

  const handleRemoveIcon = useCallback(async () => {
    if (!sourceId) return;
    try {
      await sourceService.removeSourceIcon(sourceId);
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      toast.success('Source icon removed');
    } catch (err: any) {
      toast.error('Failed to remove icon', { description: err.message || 'An error occurred' });
    }
  }, [sourceId, queryClient]);

  const handleSaveTableNodePrefix = useCallback(async () => {
    if (!sourceId || !editTableNodePrefix.trim()) return;
    setSavingPrefix(true);
    setPrefixError(null);
    try {
      await sourceService.save({
        id: sourceId,
        table_node_prefix: editTableNodePrefix.trim(),
      });
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      setEditingTableNodePrefix(false);
    } catch (err: any) {
      setPrefixError(err?.payload?.msg || err?.message || 'Failed to save prefix');
    } finally {
      setSavingPrefix(false);
    }
  }, [sourceId, editTableNodePrefix, queryClient]);

  // Handle creating a table for snowflake (with selected table) or managed integration (with name)
  const handleCreateTable = useCallback(async () => {
    if (!sourceId) return;
    setNewTableError(null);
    setIsCreatingTable(true);
    try {
      let name = '';
      let tableName: string | undefined;

      if (isSnowflake && sfSelectedTable) {
        name = sfSelectedTable.name;
        tableName = `${sfSelectedTable.database_name}.${sfSelectedTable.schema_name}.${sfSelectedTable.name}`;
      } else if (!isSnowflake && !isFlatFile) {
        // Managed integration - use typed name
        if (!newTableName.trim()) return;
        name = newTableName.trim();
      } else {
        return;
      }

      const newNode = await pipelineNodeService.create({
        name,
        label: name,
        node_type: 'SOURCE',
        source_id: sourceId,
        table_name: tableName,
        flat_file: isFlatFile ? true : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['buildTree'] });
      setNewTableDialogOpen(false);
      setNewTableName('');
      setSfSelectedTable(null);
      navigate(`/data-model/models/${newNode.id}`);
    } catch (err: any) {
      console.error('Failed to create table:', err);
      setNewTableError(err?.message || 'Failed to create table');
    } finally {
      setIsCreatingTable(false);
    }
  }, [sourceId, newTableName, isFlatFile, isSnowflake, sfSelectedTable, queryClient, navigate]);

  // Handle flat file upload in the new table dialog
  const handleNewTableFileUpload = useCallback(async (file: UploadedFile) => {
    if (!sourceId) return;
    setFileUploadError(null);

    try {
      // Step 1: Process the file
      setFileUploadStatus('processing');
      const processResp = await fileService.process(file.id);
      const parts = await BackgroundService.getInstance().waitForJob(processResp.job_id) as any[];

      if (!parts || parts.length === 0) {
        setFileUploadError('No data found in file.');
        setFileUploadStatus(null);
        return;
      }

      const part = parts[0];
      const tableName = file.name.replace(/\.[^/.]+$/, ''); // Strip extension

      // Step 2: Create the pipeline node
      setFileUploadStatus('creating_node');
      const newNode = await pipelineNodeService.create({
        name: tableName,
        label: tableName,
        node_type: 'SOURCE',
        source_id: sourceId,
        flat_file: true,
      });

      // Step 3: Create the SRT
      setFileUploadStatus('creating_srt');
      const srtResult = await fileService.createSourceRecordType({
        name: tableName,
        sourceId,
        columns: part.headers,
      });

      // Link the SRT to the node
      await pipelineNodeService.save({
        id: newNode.id,
        source_record_type_id: srtResult.id,
      } as any);

      // Step 4: Load the data
      setFileUploadStatus('loading');
      const loadResp = await fileService.loadPart(
        part.file_id,
        part.id,
        srtResult.id,
        part.headers,
        newNode.id,
      );
      await BackgroundService.getInstance().waitForJob(loadResp.job_id);

      // Done - invalidate and navigate
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['buildTree'] });
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setFileUploadStatus(null);
      setNewTableDialogOpen(false);
      navigate(`/data-model/models/${newNode.id}`);
    } catch (err: any) {
      console.error('File upload/process error:', err);
      setFileUploadError(err?.message || 'Failed to process file');
      setFileUploadStatus(null);
    }
  }, [sourceId, queryClient, navigate]);

  const pipelineNodeFilters = useMemo(() => {
    if (!source) return {};
    return {
      node_type: 'SOURCE',
      // A source can be attached to a node as either the primary owner
      // (source_id) or a secondary contributor (secondary_source_ids), e.g.
      // when multiple Shopify sources share table_node_prefix.
      $or: [{ source_id: sourceId }, { secondary_source_ids: sourceId }],
    };
  }, [source, sourceId]);

  const { data: nodesData, isLoading: isLoadingNodes } = usePipelineNodes({
    pageSize: 500,
    filters: pipelineNodeFilters,
    enabled: !!source,
  });

  // Fetch ALL source nodes for the manage dialog
  const { data: allSourceNodesData, isLoading: isLoadingAllNodes } = usePipelineNodes({
    pageSize: 500,
    filters: { node_type: 'SOURCE' },
    enabled: manageDialogOpen,
  });

  // Fetch all sources for displaying current assignment names
  const { data: allSourcesData } = useSources({ enabled: manageDialogOpen });

  // Build source name lookup
  const sourceNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of allSourcesData?.records || []) {
      map[s.id] = s.name;
    }
    return map;
  }, [allSourcesData?.records]);

  // Initialize selected node IDs when dialog opens and data loads
  useEffect(() => {
    if (manageDialogOpen && allSourceNodesData?.records) {
      const assigned = new Set<string>();
      for (const node of allSourceNodesData.records) {
        if (node.source_id === sourceId) {
          assigned.add(node.id);
        }
      }
      setSelectedNodeIds(assigned);
    }
  }, [manageDialogOpen, allSourceNodesData?.records, sourceId]);

  const nodes = useMemo(() => {
    const allNodes = nodesData?.records || [];
    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? allNodes.filter((n: PipelineNode) =>
          (n.label || n.name || '').toLowerCase().includes(query)
        )
      : allNodes;
    return [...filtered].sort((a, b) =>
      (a.label || a.name || '').localeCompare(b.label || b.name || '')
    );
  }, [nodesData?.records, searchQuery]);

  const selectedNode = useMemo(
    () => nodes.find((n: PipelineNode) => n.id === selectedNodeId),
    [nodes, selectedNodeId]
  );

  // Compute common prefix across all node labels to strip from display
  const commonPrefix = useMemo(() => {
    // If source has an explicit table_node_prefix, use that (with trailing _)
    if (source?.table_node_prefix) {
      return source.table_node_prefix.toUpperCase() + '_';
    }
    // Otherwise, compute the longest common prefix from all node labels (need >1 to compare)
    const allNodes = nodesData?.records || [];
    if (allNodes.length <= 1) return '';
    const labels = allNodes.map((n: PipelineNode) => n.label || n.name || '');
    let prefix = labels[0];
    for (let i = 1; i < labels.length; i++) {
      while (labels[i].indexOf(prefix) !== 0) {
        prefix = prefix.slice(0, -1);
        if (!prefix) return '';
      }
    }
    // Only strip prefix if it ends with _ (so we remove e.g. "SRC_DC305_" not partial words)
    const lastUnderscore = prefix.lastIndexOf('_');
    return lastUnderscore > 0 ? prefix.slice(0, lastUnderscore + 1) : '';
  }, [source?.table_node_prefix, nodesData?.records]);

  const getNodeDisplayLabel = useCallback((node: PipelineNode) => {
    const fullLabel = node.label || node.name || 'Unnamed';
    if (commonPrefix && fullLabel.toUpperCase().startsWith(commonPrefix.toUpperCase())) {
      return fullLabel.slice(commonPrefix.length);
    }
    return fullLabel;
  }, [commonPrefix]);

  // Filtered and sorted nodes for the manage dialog
  const dialogNodes = useMemo(() => {
    const allNodes = allSourceNodesData?.records || [];
    const query = dialogSearch.toLowerCase().trim();
    const filtered = query
      ? allNodes.filter((n: PipelineNode) =>
          (n.label || n.name || '').toLowerCase().includes(query)
        )
      : allNodes;
    return [...filtered].sort((a, b) =>
      (a.label || a.name || '').localeCompare(b.label || b.name || '')
    );
  }, [allSourceNodesData?.records, dialogSearch]);

  // Group snowflake tables by database -> schema, with search filtering
  const sfGrouped = useMemo(() => {
    if (!snowflakeTables) return {};
    const q = sfSearch.toLowerCase().trim();
    const filtered = q
      ? snowflakeTables.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            t.schema_name.toLowerCase().includes(q) ||
            t.database_name.toLowerCase().includes(q)
        )
      : snowflakeTables;

    const grouped: Record<string, Record<string, SnowflakeTableOption[]>> = {};
    for (const t of filtered) {
      if (!grouped[t.database_name]) grouped[t.database_name] = {};
      if (!grouped[t.database_name][t.schema_name]) grouped[t.database_name][t.schema_name] = [];
      grouped[t.database_name][t.schema_name].push(t);
    }

    // Sort tables within each schema
    for (const db of Object.keys(grouped)) {
      for (const schema of Object.keys(grouped[db])) {
        grouped[db][schema].sort((a, b) => a.name.localeCompare(b.name));
      }
    }
    return grouped;
  }, [snowflakeTables, sfSearch]);

  const toggleDb = (db: string) => {
    setExpandedDbs((prev) => {
      const next = new Set(prev);
      if (next.has(db)) next.delete(db);
      else next.add(db);
      return next;
    });
  };

  const toggleSchema = (key: string) => {
    setExpandedSchemas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleToggleNode = (nodeId: string) => {
    setSelectedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleSaveAssignments = async () => {
    if (!sourceId) return;
    setIsSaving(true);
    try {
      await sourceService.assignNodes(sourceId, Array.from(selectedNodeIds));
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      setManageDialogOpen(false);
    } catch (e) {
      console.error('Failed to assign nodes:', e);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingSource) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading source...</p>
        </div>
      </div>
    );
  }

  if (!source) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Source not found.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: '100vh' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 page-header-surface flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/sources">Sources</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              {selectedNode ? (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={`/sources/${sourceId}`}>{source.name}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{getNodeDisplayLabel(selectedNode)}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              ) : (
                <BreadcrumbItem>
                  <BreadcrumbPage>{source.name}</BreadcrumbPage>
                </BreadcrumbItem>
              )}
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex items-center gap-3">
            <SourceIcon sourceId={source.id} size={24} />
            <h1 className="text-xl font-semibold text-foreground">{source.name}</h1>
            {getSourceTypeBadge(source.type)}
            {(() => {
              const shape = isInDraftMode ? source.draft_shape : source.prod_shape;
              return shape?.total_source_records ? (
                <Badge variant="secondary">
                  {summarizeNumber(shape.total_source_records)} records
                </Badge>
              ) : null;
            })()}
          </div>
        </div>
      </div>

      {/* Main Content Area with Sidebar */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <div className="w-80 border-r border-border flex flex-col flex-shrink-0 h-full">
          <div className="overflow-auto p-4 flex-1">
            <div className="flex items-center gap-4 mb-3">
              <h2 className="text-sm font-semibold text-foreground shrink-0">Source Tables</h2>
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-7 pl-6 text-xs"
                />
              </div>
            </div>
            {isLoadingNodes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              </div>
            ) : nodes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tables found.</p>
            ) : (
              <div className="space-y-1">
                {nodes.map((node: PipelineNode) => {
                  const fullLabel = node.label || node.name || 'Unnamed';
                  const displayLabel = commonPrefix && fullLabel.startsWith(commonPrefix)
                    ? fullLabel.slice(commonPrefix.length)
                    : fullLabel;
                  return (
                  <button
                    key={node.id}
                    onClick={() => navigate(`/sources/${sourceId}/${node.id}`)}
                    title={fullLabel}
                    className={`w-full text-left px-2 py-1 rounded-md text-sm flex items-center gap-2 transition-colors ${
                      selectedNodeId === node.id
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50 text-foreground'
                    }`}
                  >
                    {getPipelineNodeIcon(node.node_type)}
                    <span className="truncate flex-1">{displayLabel}</span>
                    {node.shape?.total_records != null && (
                      <Badge variant="secondary" className="ml-auto shrink-0 text-[10px] px-1.5 py-0 h-4 font-normal">
                        {summarizeNumber(node.shape.total_records)}
                      </Badge>
                    )}
                  </button>
                  );
                })}
              </div>
            )}
          </div>
          {/* Sidebar Actions (develop mode only) */}
          {isInDraftMode && (
            <div className="p-4 border-t border-border space-y-2">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setNewTableName('');
                  setNewTableError(null);
                  setSfSearch('');
                  setSfSelectedTable(null);
                  setFileUploadStatus(null);
                  setFileUploadError(null);
                  // Auto-expand all databases when there's search or few of them
                  if (snowflakeTables) {
                    const dbs = new Set(snowflakeTables.map((t) => t.database_name));
                    setExpandedDbs(dbs.size <= 3 ? dbs : new Set());
                  } else {
                    setExpandedDbs(new Set());
                  }
                  setExpandedSchemas(new Set());
                  setNewTableDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Table
              </Button>
              <Button
                variant="outline"
                className="w-full"
                disabled={!!source && UNIONED_SOURCE_TYPES.has(source.type)}
                title={
                  source && UNIONED_SOURCE_TYPES.has(source.type)
                    ? 'Table assignment for this source type is managed automatically via shared/secondary sources and cannot be edited here.'
                    : undefined
                }
                onClick={() => {
                  setDialogSearch('');
                  setManageDialogOpen(true);
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                Manage Tables
              </Button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className={`flex-1 min-w-0 ${selectedNode ? 'flex flex-col' : 'overflow-y-auto p-8'}`}>
          {selectedNode ? (
            <Tabs defaultValue={isFlatFile ? 'files' : 'structure'} className="h-full flex flex-col">
              <div className="px-4 pt-4 space-y-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getPipelineNodeIcon(selectedNode.node_type)}
                    <h2 className="text-lg font-semibold text-foreground">
                      {getNodeDisplayLabel(selectedNode)}
                    </h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {isInDraftMode && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                      >
                        <Link to={`/data-model/models/${selectedNode.id}`}>
                          <Settings className="h-4 w-4 mr-2" />
                          Configure
                        </Link>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRebuildModalOpen(true)}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Data
                    </Button>
                  </div>
                </div>
                {selectedNode.description && (
                  <p className="text-sm text-muted-foreground">{selectedNode.description}</p>
                )}
              </div>
              <TabsList className="mx-4 mt-4">
                {isFlatFile && (
                  <TabsTrigger value="files">Files</TabsTrigger>
                )}
                <TabsTrigger value="structure">Structure</TabsTrigger>
                <TabsTrigger value="data">Data</TabsTrigger>
              </TabsList>
              {isFlatFile && (
                <TabsContent value="files" className="flex-1 overflow-auto px-4 py-4 mt-4">
                  <NodeFiles node={selectedNode} sourceId={sourceId!} />
                </TabsContent>
              )}
              <TabsContent value="structure" className="flex-1 overflow-auto px-4 py-4 mt-4">
                {selectedNode.fields && selectedNode.fields.length > 0 ? (
                  <div className="space-y-1">
                    {selectedNode.fields.map((field) => (
                      <div
                        key={field.id || field.name}
                        className="px-3 py-1.5 bg-muted rounded-md text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span className="truncate">{field.name || field.label || '-'}</span>
                          {field.type && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {field.type}
                            </span>
                          )}
                        </div>
                        {field.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {field.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No columns defined yet.</p>
                )}
              </TabsContent>
              <TabsContent value="data" className="flex-1 overflow-hidden px-4 py-4 mt-4">
                <SourceDataGate nodeType={selectedNode.node_type}>
                  <NodeDataTable pipelineNodeId={selectedNode.id} />
                </SourceDataGate>
              </TabsContent>
            </Tabs>
          ) : isManagedIntegration ? (
            /* ========== MANAGED INTEGRATION SETTINGS ========== */
            <div className="max-w-2xl space-y-6">
              {/* Action Bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-foreground">Source Settings</h2>
                </div>
                <div className="flex items-center gap-2">
                  {['meltano', 'pliable'].includes(sourceTypeConfig?.provider ?? '') && (
                    <>
                      {isSyncing && (() => {
                        const activeLoad = dataLoads?.find((l) =>
                          ['QUEUED', 'SYNCING'].includes(l.status?.toUpperCase() ?? '')
                        );
                        return activeLoad ? (
                          <Link
                            to={`/sources/${sourceId}/syncs/${activeLoad.id}`}
                            className="text-sm text-primary hover:underline mr-1"
                          >
                            View progress
                          </Link>
                        ) : null;
                      })()}
                      <SyncPollIndicator
                        active={isSyncing}
                        pollInterval={currentPollInterval}
                        onCheckNow={handleCheckNow}
                        expired={pollExpired}
                      />
                      <div className="flex items-center">
                        <Button
                          variant={isSyncing ? 'secondary' : 'outline'}
                          size="sm"
                          disabled={triggeringSync || isSyncing}
                          onClick={handleTriggerSync}
                          className="rounded-r-none border-r-0"
                        >
                          {triggeringSync || isSyncing ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4 mr-2" />
                          )}
                          {syncButtonText}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant={isSyncing ? 'secondary' : 'outline'}
                              size="sm"
                              disabled={triggeringSync || isSyncing}
                              className="rounded-l-none px-1.5"
                            >
                              <ChevronDownIcon className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setShowSelectiveSyncDialog(true)}>
                              Sync Selected Streams...
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setShowFullResyncDialog(true)}>
                              Full Re-Sync...
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </>
                  )}
                  <Button size="sm" onClick={handleSaveConfig} disabled={savingConfig}>
                    {savingConfig ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save
                  </Button>
                </div>
              </div>

              {/* Name / Description */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">General</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="source-name">Name</Label>
                    <Input
                      id="source-name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source-description">Description</Label>
                    <Textarea
                      id="source-description"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Add a description..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Custom Icon</Label>
                    {source?.icon_override ? (
                      <div className="flex items-center gap-3">
                        <img src={source.icon_override} alt="Source icon" className="h-8 w-8 object-contain rounded" />
                        <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => setIconUploadModalOpen(true)}>
                          Change Icon
                        </Button>
                        <Button variant="link" size="sm" className="p-0 h-auto text-destructive" onClick={handleRemoveIcon}>
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => setIconUploadModalOpen(true)}>
                          Set Custom Icon
                        </Button>
                      </div>
                    )}
                  </div>
                  {['meltano', 'pliable'].includes(sourceTypeConfig?.provider ?? '') && (
                    <div className="space-y-2">
                      {editingTableNodePrefix ? (
                        <div className="space-y-2">
                          <Label htmlFor="table-node-prefix">Table Node Prefix</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="table-node-prefix"
                              value={editTableNodePrefix}
                              onChange={(e) => { setEditTableNodePrefix(e.target.value); setPrefixError(null); }}
                              className="font-mono text-sm"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveTableNodePrefix();
                                if (e.key === 'Escape') setEditingTableNodePrefix(false);
                              }}
                            />
                            <Button
                              size="sm"
                              onClick={handleSaveTableNodePrefix}
                              disabled={savingPrefix || !editTableNodePrefix.trim()}
                            >
                              {savingPrefix ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingTableNodePrefix(false)}
                            >
                              Cancel
                            </Button>
                          </div>
                          {prefixError && (
                            <p className="text-xs text-destructive">{prefixError}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Controls how source node tables are named. Changing this will affect future syncs.
                          </p>
                        </div>
                      ) : (
                        <p
                          className="text-xs text-muted-foreground cursor-default select-none"
                          onDoubleClick={() => {
                            setEditTableNodePrefix(source?.table_node_prefix || '');
                            setPrefixError(null);
                            setEditingTableNodePrefix(true);
                          }}
                          title="Double-click to edit"
                        >
                          Table prefix: {source?.table_node_prefix ? (
                            <span className="font-mono">{source.table_node_prefix}</span>
                          ) : (
                            <span className="italic text-muted-foreground/60">not set</span>
                          )}
                        </p>
                      )}
                    </div>
                  )}
                  {isDirty && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSaveSource}
                      disabled={updateSource.isPending}
                    >
                      {updateSource.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Details'
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Authorization (OAuth sources) */}
              {sourceTypeConfig?.oauth && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Authorization</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {source?.oauth_last_authorized ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {(() => {
                            const warning = oauthExpiryWarning(source.oauth_expires_at);
                            if (!warning) {
                              return (
                                <>
                                  <ShieldCheck className="h-4 w-4 text-green-600" />
                                  <span className="text-sm font-medium text-green-700 dark:text-green-400">Connected</span>
                                </>
                              );
                            }
                            return (
                              <>
                                <ShieldAlert
                                  className={`h-4 w-4 ${warning.expired ? 'text-red-600' : 'text-amber-600'}`}
                                />
                                <span
                                  className={`text-sm font-medium ${
                                    warning.expired
                                      ? 'text-red-700 dark:text-red-400'
                                      : 'text-amber-700 dark:text-amber-400'
                                  }`}
                                >
                                  {warning.message}
                                </span>
                              </>
                            );
                          })()}
                          <span className="text-xs text-muted-foreground">
                            (authorized {new Date(source.oauth_last_authorized).toLocaleDateString()})
                          </span>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              Options
                              <ChevronDownIcon className="h-3 w-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleAuthorize}>
                              Re-Authorize {sourceTypeConfig.title}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleShareableLink}>
                              <Link2 className="h-4 w-4 mr-2" />
                              Create Shareable Link
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          This source requires OAuth authorization before it can sync data.
                        </p>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm">
                              Authorize
                              <ChevronDownIcon className="h-3 w-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleAuthorize}>
                              Authorize {sourceTypeConfig.title}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleShareableLink}>
                              <Link2 className="h-4 w-4 mr-2" />
                              Create Shareable Link
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Configuration Fields */}
              {sourceTypeConfig?.additional_config && sourceTypeConfig.additional_config.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(() => {
                      const fields = sourceTypeConfig.additional_config;
                      const hasMeltanoStreams = fields.some((f) => f.field_type === 'MELTANO_STREAMS');
                      const visibleFields = hasMeltanoStreams
                        ? fields.filter((f) => f.name !== 'streams')
                        : fields;
                      return visibleFields.map((field) => (
                        <ConfigFieldEditor
                          key={field.name}
                          configField={field}
                          value={config[field.name] ?? ''}
                          onChange={(val) => changeConfig(field.name, val)}
                          sourceId={sourceId}
                          config={config}
                          {...(field.field_type === 'MELTANO_STREAMS' ? { legacyStreams: config['streams'] ?? '' } : {})}
                        />
                      ));
                    })()}
                    {sourceTypeConfig.supports_catalog_discovery &&
                      !sourceTypeConfig.additional_config.some((f) => f.field_type === 'MELTANO_STREAMS') && (
                        <div className="flex items-center justify-between gap-3 border-t pt-4">
                          <p className="text-xs text-muted-foreground">
                            Save your config first, then discover to sample the files and (re)build the schema.
                          </p>
                          <DiscoverButton
                            sourceId={sourceId!}
                            onDiscovered={() => queryClient.invalidateQueries({ queryKey: ['source', sourceId] })}
                          />
                        </div>
                      )}
                  </CardContent>
                </Card>
              )}

              {/* Secure Configuration Fields */}
              {sourceTypeConfig?.additional_secret_config && sourceTypeConfig.additional_secret_config.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Secure Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                      This data is stored securely and cannot be displayed. Enter new values to update.
                    </p>
                    {sourceTypeConfig.additional_secret_config.map((field) => (
                      <ConfigFieldEditor
                        key={field.name}
                        configField={field}
                        value={secretConfig[field.name] ?? ''}
                        onChange={(val) => changeSecretConfig(field.name, val)}
                        secure
                      />
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Advanced Configuration Fields */}
              {sourceTypeConfig?.advanced_config && sourceTypeConfig.advanced_config.length > 0 && (
                <Card>
                  <Collapsible open={advancedConfigOpen} onOpenChange={setAdvancedConfigOpen}>
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="flex-row items-center justify-between">
                        <CardTitle className="text-base">Advanced Config</CardTitle>
                        {advancedConfigOpen ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="space-y-4">
                        <p className="text-xs text-muted-foreground">
                          Optional tuning for how this source syncs. Leave a field blank to use its default.
                        </p>
                        {sourceTypeConfig.advanced_config.map((field) => (
                          <ConfigFieldEditor
                            key={field.name}
                            configField={field}
                            value={config[field.name] ?? ''}
                            onChange={(val) => changeConfig(field.name, val)}
                            sourceId={sourceId}
                          />
                        ))}
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              )}

              {/* Create source tables now (mid-sync helper) */}
              {isManagedIntegration && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Source Tables</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Create source-table nodes for streams that have already synced — useful for
                      modeling while a first sync is still running or only partially complete. Safe to
                      run anytime; it won't affect an in-progress sync or mark it complete.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCreateSourceNodes}
                      disabled={creatingSourceNodes}
                    >
                      {creatingSourceNodes ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Database className="h-4 w-4 mr-2" />
                      )}
                      Create source tables from synced data
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* QuickBooks Web Connector Setup */}
              {source?.type === 'plb_quickbooks_desktop' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">QuickBooks Web Connector</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Download the .qwc configuration file and add it to the QuickBooks Web Connector on the machine running QuickBooks Desktop.
                    </p>
                    <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
                      <li>Save your configuration above and click "Download .qwc File"</li>
                      <li>Open the QuickBooks Web Connector on your Windows machine</li>
                      <li>Click "Add an application" and select the downloaded .qwc file</li>
                      <li>Enter the QBWC password you configured above when prompted</li>
                    </ol>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const { default: AuthService } = await import('../../services/auth.service');
                          const { default: ApiService } = await import('../../services/api.service');
                          const api = ApiService.getInstance();
                          const response = await fetch(
                            `${api.apiUrl()}/qbwc/download-qwc/${sourceId}`,
                            {
                              headers: {
                                'Authorization': 'Bearer ' + AuthService.getInstance().getAccessToken(),
                              },
                            }
                          );
                          if (!response.ok) throw new Error('Download failed');
                          const blob = await response.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${source?.name?.replace(/\s+/g, '_') || 'quickbooks'}_pliable.qwc`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                          toast.success('Downloaded .qwc file');
                        } catch {
                          toast.error('Failed to download .qwc file');
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download .qwc File
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Sync Schedule — not applicable to QBD, which is driven by the
                  QuickBooks Web Connector polling us rather than a cron we run. */}
              {source?.type !== 'plb_quickbooks_desktop' && (!sourceTypeConfig?.oauth || source?.oauth_last_authorized) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sync Schedule</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SyncScheduleSelect
                      value={syncCronExpression}
                      onChange={setSyncCronExpression}
                      scheduleEnabled={!isPaused}
                      onToggle={handleTogglePause}
                      disabled={togglingPause}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Sync History */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Sync History</CardTitle>
                </CardHeader>
                <CardContent>
                  <SyncHistoryTable sourceId={sourceId!} />
                </CardContent>
              </Card>

              {/* Request Secure Config Details */}
              {sourceTypeConfig && ((sourceTypeConfig.additional_config && sourceTypeConfig.additional_config.length > 0) || (sourceTypeConfig.additional_secret_config && sourceTypeConfig.additional_secret_config.length > 0)) && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Request Secure Config Details</p>
                        <p className="text-xs text-muted-foreground">Create a secure, expiring link for a third party to provide credentials.</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setSecureConfigDialogOpen(true)}>
                        <Link2 className="h-4 w-4 mr-2" />
                        Request Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Build Source */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Build Source</p>
                      <p className="text-xs text-muted-foreground">Build all nodes for this source, optionally including everything downstream.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setSourceBuildOpen(true)}>
                      <Hammer className="h-4 w-4 mr-2" />
                      Build
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Delete Source */}
              <Card className="border-destructive/30">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-destructive">Delete Source</p>
                      <p className="text-xs text-muted-foreground">Permanently remove this source and all associated data.</p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            /* ========== DEFAULT SOURCE SETTINGS ========== */
            <div className="max-w-lg space-y-6">
              <h2 className="text-lg font-semibold text-foreground">Source Settings</h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="source-name">Name</Label>
                  <Input
                    id="source-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source-description">Description</Label>
                  <Textarea
                    id="source-description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Add a description..."
                    rows={4}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Custom Icon</Label>
                  {source?.icon_override ? (
                    <div className="flex items-center gap-3">
                      <img src={source.icon_override} alt="Source icon" className="h-8 w-8 object-contain rounded" />
                      <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => setIconUploadModalOpen(true)}>
                        Change Icon
                      </Button>
                      <Button variant="link" size="sm" className="p-0 h-auto text-destructive" onClick={handleRemoveIcon}>
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => setIconUploadModalOpen(true)}>
                        Set Custom Icon
                      </Button>
                    </div>
                  )}
                </div>
                <Button
                  onClick={handleSaveSource}
                  disabled={!isDirty || updateSource.isPending}
                >
                  {updateSource.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>

              {/* Request New File Upload (flat file sources only) */}
              {isFlatFile && (
                <RequireRole role="Builder">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Request New File Upload</p>
                          <p className="text-xs text-muted-foreground">Create a shareable link for a third party to upload files to this source.</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setFlatFileUploadDialogOpen(true)}>
                          <Link2 className="h-4 w-4 mr-2" />
                          Create Link
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </RequireRole>
              )}

              {/* Build Source */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Build Source</p>
                      <p className="text-xs text-muted-foreground">Build all nodes for this source, optionally including everything downstream.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setSourceBuildOpen(true)}>
                      <Hammer className="h-4 w-4 mr-2" />
                      Build
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Delete Source */}
              <Card className="border-destructive/30">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-destructive">Delete Source</p>
                      <p className="text-xs text-muted-foreground">Permanently remove this source and all associated data.</p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Flat File Upload Link Dialog */}
      <Dialog open={flatFileUploadDialogOpen} onOpenChange={setFlatFileUploadDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Private Upload Link</DialogTitle>
            <DialogDescription>
              Anyone with this private link can upload files to <strong>{source?.name}</strong>. The link expires after 30 days.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="upload-link-title">Title</Label>
              <Input
                id="upload-link-title"
                value={flatFileUploadTitle}
                onChange={(e) => setFlatFileUploadTitle(e.target.value)}
                placeholder="e.g. Monthly Sales Report"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="upload-link-description">Description (optional)</Label>
              <Textarea
                id="upload-link-description"
                value={flatFileUploadDescription}
                onChange={(e) => setFlatFileUploadDescription(e.target.value)}
                placeholder="Instructions for the person uploading..."
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="upload-link-allow-multiple"
                checked={flatFileUploadAllowMultiple}
                onChange={(e) => setFlatFileUploadAllowMultiple(e.target.checked)}
                className="rounded border-border"
              />
              <Label htmlFor="upload-link-allow-multiple" className="text-sm font-normal">
                Allow multiple uploads
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="upload-link-ask-description"
                checked={flatFileUploadAskForDescription}
                onChange={(e) => setFlatFileUploadAskForDescription(e.target.checked)}
                className="rounded border-border"
              />
              <Label htmlFor="upload-link-ask-description" className="text-sm font-normal">
                Ask uploader to describe the file
              </Label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlatFileUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateFlatFileUploadLink}
              disabled={!flatFileUploadTitle.trim() || creatingFlatFileUploadLink}
            >
              {creatingFlatFileUploadLink ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Private Link'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Tables Dialog */}
      <Dialog open={manageDialogOpen} onOpenChange={(open) => {
        setManageDialogOpen(open);
        if (!open) setDialogSearch('');
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Manage Tables</DialogTitle>
            <DialogDescription>
              Select the source tables to assign to <strong>{source.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search tables..."
                value={dialogSearch}
                onChange={(e) => setDialogSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex-1 overflow-y-auto min-h-0 border rounded-md">
              {isLoadingAllNodes ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
              ) : dialogNodes.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4">No source tables found.</p>
              ) : (
                <div className="divide-y">
                  {dialogNodes.map((node: PipelineNode) => {
                    const isChecked = selectedNodeIds.has(node.id);
                    const otherSourceId = node.source_id && node.source_id !== sourceId ? node.source_id : null;
                    const otherSourceName = otherSourceId ? sourceNameMap[otherSourceId] : null;

                    return (
                      <label
                        key={node.id}
                        className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-accent/50 transition-colors"
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => handleToggleNode(node.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {getNodeDisplayLabel(node)}
                          </div>
                          {otherSourceName && !isChecked && (
                            <div className="text-xs text-muted-foreground">
                              Currently assigned to: {otherSourceName}
                            </div>
                          )}
                          {otherSourceName && isChecked && (
                            <div className="text-xs text-amber-600 dark:text-amber-400">
                              Will be reassigned from: {otherSourceName}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAssignments} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Table Dialog */}
      <Dialog
        open={newTableDialogOpen}
        onOpenChange={(open) => {
          if (!open && fileUploadStatus) return; // Don't close while processing
          setNewTableDialogOpen(open);
        }}
      >
        <DialogContent
          className={isSnowflake ? 'sm:max-w-[600px] max-h-[80vh] flex flex-col' : 'sm:max-w-[500px]'}
          hideClose={!!fileUploadStatus || isCreatingTable}
          onInteractOutside={(e) => { if (fileUploadStatus || isCreatingTable) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (fileUploadStatus || isCreatingTable) e.preventDefault(); }}
        >
          {/* ========== SNOWFLAKE: Table Picker ========== */}
          {isSnowflake && (
            <>
              <DialogHeader>
                <DialogTitle>New Table</DialogTitle>
                <DialogDescription>
                  Select a Snowflake table or view to add as a source table.
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
              <div className="relative mb-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search tables..."
                  value={sfSearch}
                  onChange={(e) => setSfSearch(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 border rounded-md">
                {isLoadingSnowflakeTables ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading tables...</span>
                  </div>
                ) : Object.keys(sfGrouped).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    {sfSearch ? 'No tables match your search.' : 'No available tables found.'}
                  </p>
                ) : (
                  <div className="py-1">
                    {Object.keys(sfGrouped).sort().map((db) => {
                      const dbExpanded = expandedDbs.has(db);
                      return (
                        <div key={db}>
                          <button
                            className="flex items-center gap-1.5 w-full px-3 py-1.5 text-sm font-semibold text-foreground hover:bg-accent/50 transition-colors"
                            onClick={() => toggleDb(db)}
                          >
                            {dbExpanded ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />}
                            <Database className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                            {db}
                          </button>
                          {dbExpanded && Object.keys(sfGrouped[db]).sort().map((schema) => {
                            const schemaKey = `${db}.${schema}`;
                            const schemaExpanded = expandedSchemas.has(schemaKey);
                            return (
                              <div key={schemaKey}>
                                <button
                                  className="flex items-center gap-1.5 w-full pl-7 pr-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent/50 transition-colors"
                                  onClick={() => toggleSchema(schemaKey)}
                                >
                                  {schemaExpanded ? <ChevronDown className="h-3 w-3 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 flex-shrink-0" />}
                                  {schema}
                                </button>
                                {schemaExpanded && sfGrouped[db][schema].map((table) => {
                                  const isSelected = sfSelectedTable?.name === table.name
                                    && sfSelectedTable?.schema_name === table.schema_name
                                    && sfSelectedTable?.database_name === table.database_name;
                                  return (
                                    <button
                                      key={`${db}.${schema}.${table.name}`}
                                      className={`flex items-center justify-between w-full pl-14 pr-3 py-1.5 text-sm transition-colors ${
                                        isSelected
                                          ? 'bg-primary/10 text-primary font-medium'
                                          : 'text-foreground hover:bg-accent/50'
                                      }`}
                                      onClick={() => setSfSelectedTable(table)}
                                    >
                                      <span className="truncate">{table.name}</span>
                                      {table.rows > 0 && (
                                        <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                                          {table.rows.toLocaleString()} rows
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {sfSelectedTable && (
                <div className="text-sm text-muted-foreground px-1">
                  Selected: <span className="font-medium text-foreground">{sfSelectedTable.database_name}.{sfSelectedTable.schema_name}.{sfSelectedTable.name}</span>
                </div>
              )}
              {newTableError && (
                <p className="text-sm text-destructive">{newTableError}</p>
              )}
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNewTableDialogOpen(false)} disabled={isCreatingTable}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTable} disabled={!sfSelectedTable || isCreatingTable}>
                  {isCreatingTable ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}

          {/* ========== FLAT FILE: Upload Flow ========== */}
          {isFlatFile && (
            <>
              <DialogHeader>
                <DialogTitle>New Table</DialogTitle>
                <DialogDescription>
                  Upload a file to create a new source table. The table name will be derived from the file name.
                </DialogDescription>
              </DialogHeader>
              <DialogBody className="py-2">
                {fileUploadStatus === 'processing' && (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Processing file...</span>
                  </div>
                )}
                {fileUploadStatus === 'creating_node' && (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Creating table...</span>
                  </div>
                )}
                {fileUploadStatus === 'creating_srt' && (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Setting up data source...</span>
                  </div>
                )}
                {fileUploadStatus === 'loading' && (
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Loading data into Snowflake...</span>
                  </div>
                )}
                {fileUploadError && (
                  <div className="p-3 bg-destructive/10 border border-destructive/50 rounded-md mb-2">
                    <p className="text-sm text-destructive">{fileUploadError}</p>
                  </div>
                )}
                {!fileUploadStatus && (
                  <>
                    <S3Uploader onUploadComplete={handleNewTableFileUpload} />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported formats: CSV, Excel (XLSX), TSV. Max file size: 250MB.
                    </p>
                  </>
                )}
              </DialogBody>
              {!fileUploadStatus && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNewTableDialogOpen(false)}>
                    Cancel
                  </Button>
                </DialogFooter>
              )}
            </>
          )}

          {/* ========== MANAGED INTEGRATION: Name Input ========== */}
          {!isSnowflake && !isFlatFile && (
            <>
              <DialogHeader>
                <DialogTitle>New Table</DialogTitle>
                <DialogDescription>
                  Create a new source table for <strong>{source.name}</strong>.
                </DialogDescription>
              </DialogHeader>
              <DialogBody className="space-y-2">
                <Label htmlFor="new-table-name">Table Name</Label>
                <Input
                  id="new-table-name"
                  value={newTableName}
                  onChange={(e) => setNewTableName(e.target.value)}
                  placeholder="e.g. Transactions"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newTableName.trim()) {
                      handleCreateTable();
                    }
                  }}
                  autoFocus
                />
                {newTableError && (
                  <p className="text-sm text-destructive">{newTableError}</p>
                )}
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNewTableDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTable} disabled={!newTableName.trim() || isCreatingTable}>
                  {isCreatingTable ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {selectedNode && (
        <RebuildModal
          node={selectedNode}
          open={rebuildModalOpen}
          onOpenChange={setRebuildModalOpen}
          defaultForce
          defaultCheckForNewRecords
          autoStart
        />
      )}

      {source && (
        <BuildAllModal
          source={{ id: source.id, name: source.name }}
          sourceNodeNames={(nodesData?.records || []).map((n) => n.name).filter((n): n is string => !!n)}
          open={sourceBuildOpen}
          onOpenChange={setSourceBuildOpen}
        />
      )}

      {isManagedIntegration && sourceId && (
        <FullResyncDialog
          sourceId={sourceId}
          open={showFullResyncDialog}
          onOpenChange={setShowFullResyncDialog}
          onConfirm={handleFullResync}
        />
      )}

      {isManagedIntegration && sourceId && (
        <FullResyncDialog
          mode="selective"
          sourceId={sourceId}
          open={showSelectiveSyncDialog}
          onOpenChange={setShowSelectiveSyncDialog}
          onConfirm={handleSelectiveSync}
        />
      )}

      {/* Delete Source Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!deleteSource.isPending) {
          setDeleteDialogOpen(open);
          if (!open) setDeleteConfirmName('');
        }
      }}>
        <DialogContent hideClose={deleteSource.isPending}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Source
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{source?.name}</strong>? This will permanently remove the source and all associated data.
              </p>
              <p>
                To confirm, type the source name below:
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4">
            <Input
              placeholder={source?.name || 'Source name'}
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleteSource.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSource}
              disabled={deleteSource.isPending || deleteConfirmName !== source?.name}
            >
              {deleteSource.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Confirm Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secure Config Link Dialog */}
      <Dialog open={secureConfigDialogOpen} onOpenChange={(open) => {
        setSecureConfigDialogOpen(open);
        if (!open) setSecureConfigSelectedFields(new Set());
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Request Secure Config Details
            </DialogTitle>
            <DialogDescription>
              Select which fields to request. A secure, expiring link will be created that you can share with a third party.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4 space-y-3 max-h-[50vh] overflow-y-auto">
            {[...(sourceTypeConfig?.additional_config || []), ...(sourceTypeConfig?.additional_secret_config || [])]
              // The certificate panel needs the signed-in app; it can't be filled in on the public link page.
              .filter((field) => field.field_type !== 'KEYPAIR_ROTATION')
              .map((field) => (
              <label key={field.name} className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={secureConfigSelectedFields.has(field.name)}
                  onCheckedChange={(checked) => {
                    setSecureConfigSelectedFields((prev) => {
                      const next = new Set(prev);
                      if (checked) {
                        next.add(field.name);
                      } else {
                        next.delete(field.name);
                      }
                      return next;
                    });
                  }}
                />
                <div>
                  <p className="text-sm font-medium">{field.label}</p>
                  <ConfigFieldDescription description={field.description} />
                </div>
              </label>
            ))}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSecureConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSecureConfigLink}
              disabled={creatingSecureLink || secureConfigSelectedFields.size === 0}
            >
              {creatingSecureLink ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Secure Link'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={iconUploadModalOpen} onOpenChange={setIconUploadModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Custom Icon</DialogTitle>
            <DialogDescription>
              Upload an image to use as this source's icon. Supported formats: PNG, JPEG, WebP, GIF, SVG.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="pb-6">
            <S3Uploader
              onUploadComplete={handleIconUpload}
              label="Drag and drop an image, or click to browse"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}
